import useSendBiEvent from '../../../hooks/useSendBiEvent';
import useContainerWithScrollArrow from '../../../hooks/useContainerWithScrollArrow';
import { Box, Stack } from '@mui/material';
import { UI_EVENTS } from '../../../services/events';
import { ConfigurationItem, hasFieldProperties } from '../../parser/ConfigurationItemTypes';
import { ConfigurationChatInputWrapper } from './configuration-chat/ConfigurationChatInputWrapper';
import { useConfigurationChat } from './configuration-chat/useConfigurationChat';
import { ConfigurationType } from './types';
import { useConfigurationChatMessages } from './configuration-chat/useConfigurationChatMessages';
import { AiChatMessages } from '../../common/ai-chat/AiChatMessages';
import { IconButton, Typography, colors } from '@sweep-io/sweep-design';
import { ArrowDown, ChevronLeft } from '@sweep-io/sweep-design/dist/icons';

interface ChatLayerProps {
  type: ConfigurationType;
  name: string;
  configurationItem: ConfigurationItem;
  crmOrgId: string;
  itemType: string;
  configurationChatObjectName: string;
  onClose: () => void;
}

export const ChatLayer = ({
  type,
  name,
  configurationItem,
  crmOrgId,
  itemType,
  configurationChatObjectName,
  onClose,
}: ChatLayerProps) => {
  const {
    onConfirmOrgChatForConfiguration,
    onConfirmOrgChatForFields,
    isLoading,
    messages,
    error,
    disableInput,
  } = useConfigurationChat();

  const { messagesToRender, renderChatPaywallBlockMessage, renderInitialFetchMessage } =
    useConfigurationChatMessages({ messages });

  const lastMessage =
    messagesToRender.length > 0 ? messagesToRender[messagesToRender.length - 1] : null;
  const lastMessageContent = lastMessage?.content;

  const { showScrollButton, scrollToBottom, containerRef } = useContainerWithScrollArrow({
    recalculateWhenThisChange: lastMessageContent,
  });
  const sendBiEvent = useSendBiEvent();

  return (
    <Stack height="100%" overflow="hidden">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          borderBottom: `1px solid ${colors.grey[200]}`,
        }}
      >
        <IconButton variant="flat" size="tiny" onClick={onClose}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h4-medium">{name}</Typography>
      </Box>
      <Stack
        position="relative"
        p={2}
        height="100%"
        overflow="auto"
        sx={{ overflowX: 'hidden' }}
        component="div"
        ref={containerRef as any}
      >
        <AiChatMessages
          crmOrgId={crmOrgId}
          containerRef={containerRef}
          errorMsg={error}
          messages={messagesToRender}
          renderLoader={isLoading}
          renderInitialFetchMessage={renderInitialFetchMessage}
          renderChatPaywallBlockMessage={renderChatPaywallBlockMessage}
          spacerFix={40}
        />
        {showScrollButton && (
          <Stack sx={{ position: 'absolute', bottom: '12px', width: '100%', alignItems: 'center' }}>
            <IconButton onClick={scrollToBottom} variant="system" size="small">
              <ArrowDown />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <ConfigurationChatInputWrapper
        onConfirm={async (msg: string) => {
          sendBiEvent({
            name: UI_EVENTS.documentationAiEngage,
            props: { object: configurationChatObjectName, element: type },
          });

          if (hasFieldProperties(configurationItem)) {
            onConfirmOrgChatForFields({
              message: msg,
              crmOrgId,
              objectName: configurationChatObjectName ?? '',
              fieldName: name,
            });
            return;
          }

          onConfirmOrgChatForConfiguration({
            message: msg,
            crmOrgId,
            metadataId: configurationItem.metadataId ?? '',
          });
        }}
        isLoading={isLoading}
        configurationKey={type}
        itemType={itemType}
        disableInput={disableInput}
        messagesCount={messages.length}
      />
    </Stack>
  );
};
